"use client";

import {
  Camera,
  CheckCircle2,
  Clock3,
  Flag,
  Maximize2,
  ShieldCheck,
} from "lucide-react";

const answers = [
  ["A", "The acceleration remains constant."],
  ["B", "The velocity continuously increases."],
  ["C", "The displacement remains constant."],
  ["D", "The particle moves with uniform velocity."],
] as const;

export default function ExamPreview() {
  return (
    <div className="w-full bg-white p-4 sm:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <span className="rounded-full border border-brand-gold/25 bg-brand-gold/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-brand">
          Illustrative exam preview
        </span>
        <span className="text-[10px] font-medium text-slate-400">
          Sample interface · Not a live exam
        </span>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-[0_12px_32px_rgba(15,23,42,0.07)] sm:p-6">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-brand-gold" />
          <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-brand">
            Computer-based test
          </span>
        </div>

        <div className="mt-4">
          <h2 className="text-2xl font-black leading-tight tracking-tight text-brand sm:text-3xl">
            JEE / NEET Practice Test
          </h2>
          <p className="mt-2 max-w-md text-xs leading-5 text-slate-500">
            A sample of the question, answer-selection and navigation experience.
          </p>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <StatusPill icon={<Clock3 className="h-3 w-3" />} label="Timer area" />
          <StatusPill icon={<Camera className="h-3 w-3" />} label="Camera options depend on exam settings" />
          <StatusPill icon={<Maximize2 className="h-3 w-3" />} label="Fullscreen support" />
        </div>

        <div className="mt-5 flex items-center gap-2 border-t border-slate-100 pt-4">
          <ShieldCheck className="h-4 w-4 shrink-0 text-brand-gold" />
          <p className="text-[10px] leading-4 text-slate-500">
            Available examination controls depend on the exam configuration.
          </p>
        </div>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[1.18fr_0.82fr]">
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between gap-2">
            <span className="rounded-full bg-brand px-3 py-2 text-[10px] font-bold text-white">
              Sample question · 01
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-2.5 py-2 text-[9px] font-semibold text-slate-600">
              <Flag className="h-3 w-3" /> Mark for review
            </span>
          </div>

          <p className="mt-6 text-base font-bold leading-7 tracking-tight text-brand sm:text-lg">
            A particle moves along a straight line with constant acceleration. Which statement is always true?
          </p>

          <div className="mt-5 space-y-2.5">
            {answers.map(([letter, text], index) => (
              <div
                key={letter}
                className={`flex items-start gap-3 rounded-xl border px-3 py-3 ${
                  index === 0
                    ? "border-brand bg-brand/[0.035]"
                    : "border-slate-200 bg-white"
                }`}
              >
                <span
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-[10px] font-bold ${
                    index === 0
                      ? "bg-brand text-white"
                      : "bg-slate-100 text-slate-500"
                  }`}
                >
                  {letter}
                </span>
                <p className={`pt-1 text-xs leading-5 ${index === 0 ? "font-semibold text-brand" : "text-slate-600"}`}>
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-brand-gold/20 bg-brand-gold/10 text-brand">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400">Exam controls</p>
                <p className="text-sm font-bold text-brand">Configuration-based</p>
              </div>
            </div>
            <div className="mt-5 space-y-3 text-xs text-slate-600">
              {["Question navigation", "Answer selection", "Review marking"].map((item) => (
                <div key={item} className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold text-brand">Question palette</h3>
              <span className="text-[9px] text-slate-400">Sample layout</span>
            </div>
            <div className="mt-4 grid grid-cols-5 gap-2">
              {Array.from({ length: 15 }, (_, index) => {
                const number = index + 1;
                const answered = number === 1 || number === 2 || number === 4 || number === 6;
                const current = number === 3;
                return (
                  <div
                    key={number}
                    className={`flex h-8 items-center justify-center rounded-lg text-[10px] font-semibold ${
                      current
                        ? "bg-brand text-white"
                        : answered
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    {number}
                  </div>
                );
              })}
            </div>
            <p className="mt-3 text-[10px] leading-4 text-slate-400">
              Colors are illustrative and may vary by exam state.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-[10px] font-medium text-slate-600">
      {icon}
      {label}
    </span>
  );
}
